
import { useEffect } from "react";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  ShieldCheck,
  Landmark,
  Users,
  GraduationCap,
  Eye,
  Target,
  Sparkles,
  BriefcaseBusiness,
  Contact,
  CalendarDays,
  Globe,
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const staffMembers = [
  {
    id: 1,
    image: "/assets/images/faruq.jpeg",
    name: "T.A.M. Omar Faruque",
    designation: "Assistant Registrar (Admin)",
    department: "Registrar Office",
    mobile: "01813075370",
    email: "rubelrumi2000@gmail.com",
  },
  {
    id: 2,
    image: "/assets/images/mong.jpeg",
    name: "Qu Mong Prue",
    designation: "Administrative Officer",
    department: "Registrar Office",
    mobile: "01558833486",
    email: "qmongprue@yahoo.com",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

function StaffCard({ staff }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -9 }}
      className="group overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-md transition-all duration-500 hover:border-emerald-200 hover:shadow-2xl hover:shadow-emerald-900/10"
    >
      
		<div className="relative flex h-72 w-full items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-50 via-gray-50 to-emerald-100">
		  <img
			src={`${import.meta.env.VITE_API_SOURCE_URL}${staff.image}`}
			alt={staff.name}
			loading="lazy"
			className="h-full w-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
			onError={(e) => {
			  e.currentTarget.onerror = null;
			  e.currentTarget.src = LogoFallback;
			  e.currentTarget.classList.add("p-10");
			}}
		  />

		  <span className="absolute left-4 top-4 rounded-full border border-white/60 bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 shadow-sm backdrop-blur">
			Registrar Office
		  </span>
		</div>


      <div className="p-6">
        <div className="mb-3 h-1 w-12 rounded-full bg-emerald-600 transition-all duration-500 group-hover:w-20" />

        <h3 className="text-xl font-bold text-gray-900 transition-colors group-hover:text-emerald-800">
          {staff.name}
        </h3>

        <p className="mt-2 text-sm font-semibold text-emerald-700">
          {staff.designation}
        </p>

        <p className="mt-1 text-xs text-gray-500">{staff.department}</p>

        <div className="mt-6 space-y-3 border-t border-gray-100 pt-5">
          <a
            href={`tel:${staff.mobile}`}
            className="flex items-center gap-3 text-sm text-gray-600 transition-colors hover:text-emerald-700"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
              <Phone size={16} />
            </span>
            {staff.mobile}
          </a>

          <a
            href={`mailto:${staff.email}`}
            className="flex min-w-0 items-center gap-3 text-sm text-gray-600 transition-colors hover:text-emerald-700"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
              <Mail size={16} />
            </span>
            <span className="break-all">{staff.email}</span>
          </a>
        </div>
      </div>
    </motion.div>
  );
}

function PrincipleCard({ icon: Icon, title, description, number }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -7 }}
      className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-7 shadow-sm transition-all duration-500 hover:border-emerald-200 hover:shadow-xl"
    >
      <span className="absolute -right-3 -top-8 text-8xl font-black text-emerald-50 transition-transform duration-500 group-hover:scale-110">
        {number}
      </span>

      <div className="relative z-10">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 transition-all duration-500 group-hover:rotate-6 group-hover:bg-emerald-700 group-hover:text-white">
          <Icon size={27} />
        </div>

        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <p className="mt-3 text-sm leading-7 text-gray-600">
          {description}
        </p>
      </div>
    </motion.div>
  );
}

export default function RegistrarMessage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Office of the Registrar | Bandarban University",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const registrar = {
    image: "/assets/img/jashim.jpeg",
    name: "Prof. Md. Jashim Uddin Khan",
    designation: "Registrar",
    university: "Bandarban University",
    officeMobile: "01318-305900",
    mobile: "01711244271",
    emails: ["jashimkhan1964@gmail.com", "registrar@bubban.ac.bd"],
  };

  const researchLinks = [
    { name: "Google Scholar", color: "bg-blue-600 hover:bg-blue-700" },
    { name: "ResearchGate", color: "bg-green-600 hover:bg-green-700" },
    { name: "ORCID", color: "bg-gray-700 hover:bg-gray-800" },
    { name: "Scopus", color: "bg-orange-600 hover:bg-orange-700" },
    { name: "Web of Science", color: "bg-purple-600 hover:bg-purple-700" },
  ];

  return (
    <div
      className="overflow-hidden bg-[#f7faf8] text-gray-800"
      style={{ "--university-accent": accent }}
    >
      {/* HERO SECTION */}
      <section className="relative isolate overflow-hidden bg-[#073c27] text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-[#063b27] via-[#075437]/95 to-[#08734d]/80" />

        <div className="absolute -left-32 -top-40 h-96 w-96 rounded-full border-[45px] border-emerald-300/10" />
        <div className="absolute -bottom-48 -right-20 h-[500px] w-[500px] rounded-full border-[65px] border-emerald-300/10" />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 65, repeat: Infinity, ease: "linear" }}
          className="absolute right-[12%] top-12 hidden h-64 w-64 rounded-full border border-white/10 lg:block"
        >
          <div className="absolute -left-2 top-1/2 h-4 w-4 rounded-full bg-emerald-300 shadow-lg shadow-emerald-300/50" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div
              variants={fadeUp}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-emerald-100 backdrop-blur"
            >
              <Landmark size={16} />
              Bandarban University
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-7xl"
            >
              Office of the
              <span className="block text-emerald-300">Registrar</span>
            </motion.h1>

            <motion.div
              variants={fadeUp}
              className="mt-7 h-1.5 w-24 rounded-full bg-emerald-300"
            />

            <motion.p
              variants={fadeUp}
              className="mt-7 max-w-2xl text-base leading-8 text-emerald-50/90 sm:text-lg"
            >
              Supporting institutional excellence through effective
              administration, transparent governance, and dedicated
              university services.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-9 flex flex-wrap gap-3"
            >
              <a
                href="#registrar-profile"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-emerald-900 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-50"
              >
                <Contact size={18} />
                Meet the Registrar
                <ArrowUpRight size={17} />
              </a>

              <a
                href="#office-staff"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20"
              >
                <Users size={18} />
                Our Office Team
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="absolute bottom-10 right-10 hidden lg:block"
          >
            <div className="flex h-36 w-36 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur">
              <div className="flex h-28 w-28 items-center justify-center rounded-full border border-emerald-300/40 bg-emerald-400/10">
                <BriefcaseBusiness size={46} className="text-emerald-200" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 h-2 w-full bg-gradient-to-r from-emerald-300 via-white/30 to-emerald-500" />
      </section>

      {/* REGISTRAR PROFILE */}
      <section
        id="registrar-profile"
        className="relative px-6 py-20 lg:px-10"
      >
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12 text-center"
          >
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
              Institutional Leadership
            </span>

            
            <div className="mx-auto mt-5 h-1 w-20 rounded-full bg-emerald-600" />
          </motion.div>

          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -45 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative mx-auto w-full max-w-lg"
            >
              <div className="absolute -inset-4 rounded-[2rem] bg-emerald-100/70" />
              <div className="absolute -left-7 -top-7 h-24 w-24 rounded-tl-[2rem] border-l-4 border-t-4 border-emerald-600" />

              <div className="relative overflow-hidden rounded-[1.8rem] border-8 border-white bg-white shadow-2xl">
                <img
                  src={registrar.image}
                  alt={registrar.name}
                  className="h-[390px] w-full object-cover object-top sm:h-[460px]"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = LogoFallback;
                    e.currentTarget.classList.add("object-contain", "p-10");
                  }}
                />

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-6 pt-24 text-white">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">
                    Office of the Registrar
                  </span>
                  <h3 className="mt-2 text-2xl font-bold">
                    {registrar.name}
                  </h3>
                  <p className="mt-1 text-sm text-emerald-100">
                    {registrar.designation}, {registrar.university}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 45 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:pl-5"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-widest text-emerald-800">
                <ShieldCheck size={15} />
                University Registrar
              </span>

              <h2 className="mt-6 text-3xl font-black leading-tight text-gray-900 sm:text-4xl">
                {registrar.name}
              </h2>

              <p className="mt-4 text-lg font-semibold text-emerald-700">
                Registrar, Bandarban University
              </p>

              <div className="my-7 h-1 w-16 rounded-full bg-emerald-600" />

              <p className="text-base leading-8 text-gray-600">
                The Office of the Registrar plays an important role in
                coordinating the administrative functions of Bandarban
                University. It supports institutional communication,
                administrative coordination, official documentation, and
                university-wide services to help ensure smooth and effective
                operations.
              </p>

              {/* CONTACT DETAILS */}
              <div className="mt-8 space-y-4">
                <a
                  href="tel:+8801318305900"
                  className="flex items-center gap-4 rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Phone size={21} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Office Mobile
                    </p>
                    <p className="mt-1 font-bold text-gray-800">
                      {registrar.officeMobile}
                    </p>
                  </div>
                  <ArrowUpRight className="ml-auto text-gray-300" size={18} />
                </a>

                <a
                  href="tel:+8801711244271"
                  className="flex items-center gap-4 rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Phone size={21} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Personal Mobile
                    </p>
                    <p className="mt-1 font-bold text-gray-800">
                      {registrar.mobile}
                    </p>
                  </div>
                  <ArrowUpRight className="ml-auto text-gray-300" size={18} />
                </a>

                <a
                  href="mailto:registrar@bubban.ac.bd"
                  className="flex items-center gap-4 rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Mail size={21} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Official Email
                    </p>
                    <p className="mt-1 break-all font-bold text-gray-800">
                      registrar@bubban.ac.bd
                    </p>
                  </div>
                  <ArrowUpRight className="ml-auto shrink-0 text-gray-300" size={18} />
                </a>

                <p className="break-all text-sm text-gray-500">
                  Additional Email: {registrar.emails[0]}
                </p>
              </div>

              {/* RESEARCH PROFILES */}
              <div className="mt-8">
                <h4 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-800">
                  <GraduationCap size={19} className="text-emerald-700" />
                  Academic & Research Profiles
                </h4>

                <div className="flex flex-wrap gap-2">
                  {researchLinks.map((item) => (
                    <span
                      key={item.name}
                      className={`${item.color} rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm`}
                    >
                      {item.name}
                    </span>
                  ))}
                </div>

                <p className="mt-3 text-xs leading-5 text-gray-400">
                  Profile destinations have not been supplied. Add the
                  official profile URLs to make these labels clickable.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OFFICE STAFF */}
      <section
        id="office-staff"
        className="relative bg-white px-6 py-20 lg:px-10"
      >
        <div className="absolute right-0 top-0 h-72 w-72 rounded-bl-full bg-emerald-50/70" />

        <div className="relative mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-14 flex flex-col justify-between gap-5 md:flex-row md:items-end"
          >
            <div>
              <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
                Administrative Team
              </span>
              <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
                Meet Our Office Staff
              </h2>
              <div className="mt-5 h-1 w-20 rounded-full bg-emerald-600" />
              <p className="mt-5 max-w-2xl leading-7 text-gray-500">
                Dedicated professionals supporting the day-to-day
                administrative activities of Bandarban University.
              </p>
            </div>

            <div className="flex w-fit items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-700 text-white">
                <Users size={23} />
              </div>
              <div>
                <p className="text-2xl font-black text-emerald-900">
                  {staffMembers.length}
                </p>
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Office Staff
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
            className="grid gap-8 sm:grid-cols-2"
          >
            {staffMembers.map((staff) => (
              <StaffCard key={staff.id} staff={staff} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* VISION MISSION GOALS */}
      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-12 text-center"
          >
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">
              Our Direction
            </span>

            <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
              Our Vision, Mission & Goals
            </h2>

            <div className="mx-auto mt-5 h-1 w-20 rounded-full bg-emerald-600" />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid gap-7 md:grid-cols-3"
          >
            <PrincipleCard
              icon={Eye}
              number="01"
              title="Our Vision"
              description="To become a leading institution of higher learning in Asia."
            />

            <PrincipleCard
              icon={Sparkles}
              number="02"
              title="Our Mission"
              description="To nurture creativity, research, and leadership through quality education."
            />

            <PrincipleCard
              icon={Target}
              number="03"
              title="Our Goals"
              description="Expanding research opportunities and strengthening global partnerships."
            />
          </motion.div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#06452e] via-[#08734d] to-[#0b8056] px-6 py-16 text-white lg:px-10">
        <div className="absolute -right-20 -top-32 h-96 w-96 rounded-full border-[55px] border-white/5" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto max-w-7xl"
        >
          <div className="mb-10 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-200">
              Administrative Enquiries
            </span>

            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              Office of the Registrar
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-7 text-emerald-50/80">
              Get in touch with the Registrar's Office for official
              administrative information and university-related enquiries.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:bg-white/15">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <MapPin size={23} />
              </div>
              <div>
                <h3 className="font-bold">Office Address</h3>
                <p className="mt-2 text-sm leading-6 text-emerald-50/80">
                  K.B Road, Shualok, Post Office: Shualok - 4600, Bandarban
                  Sadar, Bangladesh
                </p>
              </div>
            </div>

            <a
              href="tel:+8801318305900"
              className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/15"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <Phone size={23} />
              </div>
              <div>
                <h3 className="font-bold">Phone Number</h3>
                <p className="mt-2 text-sm text-emerald-50/80">
                  +880 1318-305900
                </p>
              </div>
            </a>

            <a
              href="mailto:registrar@bubban.ac.bd"
              className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/15"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-800">
                <Mail size={23} />
              </div>
              <div>
                <h3 className="font-bold">Email Address</h3>
                <p className="mt-2 break-all text-sm text-emerald-50/80">
                  registrar@bubban.ac.bd
                </p>
              </div>
            </a>
          </div>

          <div className="mt-10 text-center">
            <a
              href="mailto:registrar@bubban.ac.bd?subject=Registrar%20Office%20Enquiry"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 font-bold text-emerald-900 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-50"
            >
              <Mail size={18} />
              Contact Registrar Office
              <ArrowUpRight size={17} />
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
